/**
 * 报表聚合 —— 纯函数，服务端与本地模式共用。
 * 不依赖 nestjs / prisma / react；输入纯数据，输出 GetReportResponse。
 */
import type {
  Group,
  ReportType,
  GetReportResponse,
  ReportGroupSection,
} from '@daynote/shared-types';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function fmt(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 根据类型与基准日期计算报表区间。
 * @param weekStart 0=周日, 1=周一（来自 UserSetting.weekStart）
 */
export function getReportRange(
  type: ReportType,
  date: string,
  weekStart: 0 | 1 = 1,
): { start: string; end: string } {
  const parts = date.split('-').map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const d = parts[2] ?? 1;

  if (type === 'month') {
    const lastDay = new Date(y, m, 0).getDate();
    return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(lastDay)}` };
  }

  // week：以 weekStart 为一周起始
  const base = new Date(y, m - 1, d);
  const offset = (base.getDay() - weekStart + 7) % 7;
  const startDate = new Date(y, m - 1, d - offset);
  const endDate = new Date(y, m - 1, d - offset + 6);
  return { start: fmt(startDate), end: fmt(endDate) };
}

/** 聚合用的最小条目形状（服务端 / 本地都能提供） */
export interface ReportInputItem {
  id: string;
  date: string; // YYYY-MM-DD
  content: string;
  hours: number | null;
  isDone: boolean;
  groupId: string | null;
}

/**
 * 把区间内的条目按分组聚合为报表。items 须已是区间内、按需排序好的集合。
 */
export function aggregateReport(input: {
  items: ReportInputItem[];
  groups: Group[];
  type: ReportType;
  start: string;
  end: string;
}): GetReportResponse {
  const { items, groups, type, start, end } = input;
  const groupById = new Map(groups.map((g) => [g.id, g]));

  interface Section extends ReportGroupSection {
    sortOrder: number;
  }
  const map = new Map<string, Section>();

  for (const it of items) {
    const key = it.groupId ?? '__none__';
    let section = map.get(key);
    if (!section) {
      const group = it.groupId ? groupById.get(it.groupId) ?? null : null;
      section = {
        group,
        totalHours: 0,
        itemCount: 0,
        doneCount: 0,
        untrackedCount: 0,
        items: [],
        sortOrder: group?.sortOrder ?? Number.MAX_SAFE_INTEGER,
      };
      map.set(key, section);
    }
    section.itemCount += 1;
    if (it.isDone) section.doneCount += 1;
    if (it.hours == null) section.untrackedCount += 1;
    else section.totalHours += it.hours;
    section.items.push({
      id: it.id,
      date: it.date,
      content: it.content,
      hours: it.hours,
      isDone: it.isDone,
    });
  }

  const sections: ReportGroupSection[] = [...map.values()]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(({ sortOrder: _omit, ...rest }) => rest);

  return {
    type,
    start,
    end,
    totalHours: sections.reduce((s, x) => s + x.totalHours, 0),
    totalItems: items.length,
    doneItems: items.filter((it) => it.isDone).length,
    sections,
  };
}
